/**
 * Generic positioned popover with caret arrow, outside-click close.
 * Max width: 240–360px. Max height: 280–400px. Positioned below trigger.
 * Anchors right-aligned if it would overflow viewport.
 */
import { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn.js';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  /** Min width of the popover panel in px (default 240) */
  minWidth?: number;
  /** Max width of the popover panel in px (default 320) */
  maxWidth?: number;
  className?: string;
}

export function Popover({
  open,
  onClose,
  triggerRef,
  children,
  minWidth = 240,
  maxWidth = 320,
  className,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; rightAligned: boolean }>({
    top: 0,
    left: 0,
    rightAligned: false,
  });

  // Position the panel below the trigger
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;

    const tRect = triggerRef.current.getBoundingClientRect();
    const pRect = panelRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const scrollY = window.scrollY;

    const top = tRect.bottom + scrollY + 6; // 6px gap
    let left = tRect.left;
    let rightAligned = false;

    // Check if panel would overflow right edge
    if (left + pRect.width > vw - 8) {
      left = tRect.right - pRect.width;
      rightAligned = true;
    }

    // Keep it from going off the left edge
    if (left < 8) left = 8;

    setPos({ top, left, rightAligned });
  }, [open, triggerRef]);

  // Outside click handler
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      const target = e.target as Node;
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) return;
      onClose();
    }
    // Use capture to run before any inner click handlers
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open, onClose, triggerRef]);

  // Keyboard escape
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  // Caret horizontal position relative to trigger center
  const triggerRect = triggerRef.current?.getBoundingClientRect();
  const caretLeft = triggerRect
    ? triggerRect.left + triggerRect.width / 2 - pos.left
    : 20;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        minWidth,
        maxWidth,
        zIndex: 200,
      }}
      className={cn(
        'bg-white border border-border rounded-xl shadow-lg',
        'animate-popover-in',
        className
      )}
    >
      {/* Caret arrow */}
      <div
        aria-hidden="true"
        style={{ left: Math.max(12, Math.min(caretLeft - 6, (maxWidth || 320) - 24)) }}
        className="absolute -top-[7px] w-3 h-3 bg-white border-l border-t border-border rotate-45"
      />
      <div className="overflow-y-auto" style={{ maxHeight: 400 }}>
        {children}
      </div>
    </div>,
    document.body
  );
}
